<!-- eslint-disable vue/no-v-html -->
<template>
  <b-card
    class="task-card mb-2 cursor-pointer"
    no-body
    @click="$emit('click', task)"
  >
    <b-card-body class="p-2">
      <div class="d-flex justify-content-between align-items-start mb-1">
        <h6
          class="mb-0 task-title"
        >
          {{ task.title }}
        </h6>
        <b-dropdown
          variant="link"
          no-caret
          right
          @click.stop
        >
          <template #button-content>
            <feather-icon
              icon="MoreVerticalIcon"
              size="18"
              class="text-muted"
            />
          </template>
          <b-dropdown-item @click.stop="copyShareLink">
            <feather-icon
              icon="LinkIcon"
              size="14"
              class="mr-50"
            />
            {{ $t('Copy Link') }}
          </b-dropdown-item>
          <b-dropdown-item @click.stop="$emit('edit', task)">
            <feather-icon
              icon="EditIcon"
              size="14"
              class="mr-50"
            />
            {{ $t('Edit') }}
          </b-dropdown-item>
          <b-dropdown-item
            variant="danger"
            @click.stop="$emit('delete', task)"
          >
            <feather-icon
              icon="TrashIcon"
              size="14"
              class="mr-50"
            />
            {{ $t('Delete') }}
          </b-dropdown-item>
        </b-dropdown>
      </div>

      <div
        v-if="task.description"
        class="small mb-2 task-description"
        v-html="processMentionContent(renderDescriptionPreview(task.description), GET_CURRENT_BOARD?.members?.data || [])"
      />

      <div class="d-flex justify-content-between align-items-center">
        <div class="task-meta d-flex flex-column align-items-start">
          <div
            v-if="task.labels?.length"
            class="mt-25"
          >
            <span
              v-for="i in Math.min(task.labels.length, 3)"
              :key="i"
            >
              <b-badge
                v-if="labelByUid[task.labels[i - 1].uuid]"
                v-b-tooltip.hover="task.labels[i - 1].name.length > 12 ? task.labels[i - 1].name : ''"
                :style="{ backgroundColor: labelByUid[task.labels[i - 1].uuid]?.color }"
                class="mr-25"
              >
                {{ truncateText(labelByUid[task.labels[i - 1].uuid]?.name, 12) }}
              </b-badge>
            </span>
            <b-badge
              v-if="task.labels.length > 3"
              variant="light-secondary"
              class="mr-25"
            >
              +{{ task.labels.length - 3 }}
            </b-badge>
          </div>

          <div class="d-flex align-items-center mt-25">
            <b-badge
              v-if="taskPriority"
              v-b-tooltip.hover="`Priority: ${taskPriority.text}`"
              :variant="`light-${taskPriority?.color}`"
              class="d-flex align-items-center mr-50"
            >
              <feather-icon
                icon="CircleIcon"
                :style="{ strokeWidth: '5' }"
              />
              <span
                class="text-capitalize font-weight-bold"
                style="margin-left: 8px;"
              >
                {{ taskPriority?.text }}
              </span>
            </b-badge>
            <b-badge
              v-if="task.dueDate"
              :variant="isBeforeTODAY(task.dueDate) ? 'light-danger' : 'light-secondary'"
              class="d-flex align-items-center mr-50"
            >
              <feather-icon
                icon="CalendarIcon"
                size="10"
                class="mr-25"
              />
              {{ formatDate(task.dueDate) }}
            </b-badge>
          </div>
        </div>
        <div
          :id="`${task.uuid}-assignee`"
          @click.stop="setCurrentPopoverTask(task.uuid)"
        >
          <b-avatar
            v-b-tooltip="task.assignee?.name || 'Unassigned'"
            :src="getStorage(task.assignee?.avatar)"
            :text="getInitials(task.assignee?.name)"
            size="24"
            variant="light-primary"
          >
            <feather-icon
              v-if="!task.assignee"
              icon="UserPlusIcon"
              size="14"
              class="text-muted"
            />
          </b-avatar>
        </div>
      </div>
    </b-card-body>

    <!-- Change Assignee Popover -->
    <b-popover
      :target="`${task.uuid}-assignee`"
      :show="GET_CURRENT_POPOVER_TASK === task.uuid"
      triggers="click"
      custom-class="w-100"
    >
      <template #title>
        <div class="d-flex justify-content-between">
          <span>{{ $t('Change Assignee') }}</span>
          <feather-icon
            icon="XIcon"
            size="16"
            class="cursor-pointer"
            @click="setCurrentPopoverTask(null)"
          />
        </div>
      </template>
      <b-form-group>
        <template #label>
          <span v-b-tooltip.hover="task.title.length > 15 ? task.title : ''">{{ $t('Assignee for') }} {{ truncateText(task.title, 15) }}</span>
        </template>
        <b-overlay :show="isProcessing">
          <assignee-selector
            v-model="form.assignee"
            :members="GET_CURRENT_BOARD?.members?.data || []"
            @input="form.assignee = $event"
          />
        </b-overlay>

        <div class="pt-2 d-flex">
          <b-button
            @click="setCurrentPopoverTask(null)"
          >
            {{ $t('Close') }}
          </b-button>

          <b-button
            variant="primary"
            class="ml-auto"
            :disabled="isProcessing || task.assignee?.uuid === form.assignee || (!task.assignee?.uuid && !form.assignee)"
            @click="assignTask"
          >
            {{ $t('Assign') }}
          </b-button>
        </div>
      </b-form-group>
    </b-popover>
  </b-card>
</template>

<script>
import {
  BCard,
  BCardBody,
  BDropdown,
  BDropdownItem,
  BBadge,
  BAvatar,
  BPopover,
  BFormGroup,
  BButton,
  BOverlay,
  VBTooltip,
} from 'bootstrap-vue'
import boardsApi from '@/plugins/graphql/boards'
import { mapActions, mapGetters } from 'vuex'
import { mixin as clickaway } from 'vue-clickaway'
import AssigneeSelector from './AssigneeSelector.vue'
import { processMentionContent } from './markdown'
import { priorityOptions } from '../useProjectManagement'

export default {
  name: 'TaskCard',
  components: {
    BCard,
    BCardBody,
    BDropdown,
    BDropdownItem,
    BBadge,
    BAvatar,
    BPopover,
    BFormGroup,
    BOverlay,
    BButton,
    AssigneeSelector,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  mixins: [clickaway],
  props: {
    task: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      priorityOptions,
      processMentionContent,
      form: {
        assignee: null,
      },
      isProcessing: false,
    }
  },
  computed: {
    ...mapGetters('boards', ['GET_CURRENT_BOARD', 'GET_CURRENT_POPOVER_TASK', 'GET_CURRENT_BOARD_LABELS']),
    labelByUid() {
      return this.GET_CURRENT_BOARD_LABELS.reduce((map, label) => {
        // eslint-disable-next-line no-param-reassign
        map[label.uuid] = label
        return map
      }, {})
    },
    taskPriority() {
      return this.priorityOptions.find(option => option.value === this.task.priority)
    },

  },
  watch: {
    task: {
      handler(val) {
        this.form.assignee = val.assignee?.uuid || null
      },
      immediate: true,
    },
  },
  methods: {
    ...mapActions('boards', ['setCurrentPopoverTask']),
    async assignTask() {
      this.isProcessing = true
      const payload = {
        uuid: this.task.uuid,
        assigneeUid: this.form.assignee,
        projectUid: this.$store.state.project.selectedProject,
      }

      try {
        const res = await boardsApi.assignTask(payload)

        this.$emit('refresh')
        this.showSuccess(res.data.assignTask.message)
      } catch (error) {
        this.showError(error?.message || this.$t('An error occurred while assigning the task.'))
      } finally {
        this.isProcessing = false
        this.$root.$emit('bv::hide::popover')
      }
    },
    renderDescriptionPreview(text) {
      if (!text) return ''

      // Truncate first
      const truncated = text.length > 120 ? `${text.substring(0, 120)}...` : text

      // Parse markdown to HTML
      let html = truncated
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')

      // Horizontal rules (must be before headers)
      html = html.replace(/^(---|\*\*\*|___)$/gim, '<hr>')

      // Headers
      html = html.replace(/^### (.*$)/gim, '<strong>$1</strong>')
      html = html.replace(/^## (.*$)/gim, '<strong>$1</strong>')
      html = html.replace(/^# (.*$)/gim, '<strong>$1</strong>')

      // Bold
      html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      html = html.replace(/__(.+?)__/g, '<strong>$1</strong>')

      // Italic
      html = html.replace(/\*(.+?)\*/g, '<em>$1</em>')
      html = html.replace(/_(.+?)_/g, '<em>$1</em>')

      // Inline code
      html = html.replace(/`([^`]+)`/g, '<code class="inline-code">$1</code>')

      // Links (just show text, not clickable)
      html = html.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')

      // Remove code block markers if present
      html = html.replace(/```[\w]*\n?/g, '')

      // Line breaks
      html = html.replace(/\n/g, ' ')

      return html
    },
    truncateText(text, length) {
      if (!text) return ''
      return text.length > length ? `${text.substring(0, length)}...` : text
    },
    formatDate(date) {
      if (!date) return ''
      return new Date(date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      })
    },
    getInitials(name) {
      if (!name) return '?'
      return name
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    },
    copyShareLink() {
      const { boardUid } = this.$route.params
      const taskUid = this.base58Converter(this.task?.uuid)

      const shareLink = `${window.location.origin}/project-management/board/${boardUid}/tasks/${taskUid}`
      navigator.clipboard.writeText(shareLink).then(() => {
        this.linkCopied = true
      })

      this.showSuccess(this.$t('Share link copied to clipboard!'))
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@core/scss/base/core/colors/palette-variables';
@import '@core/scss/base/components/include';

.dark-layout {
  .task-card {
    background: $theme-dark-body-bg;

    .task-description {
      ::v-deep .mention {
        background-color: #1e3a8a !important;
        color: #93c5fd !important;
      }
    }

    ::v-deep .dropdown {
      .btn-link {
        &:hover {
          background: $theme-dark-card-bg !important;
        }
      }
    }
  }
}
.task-card {
  transition: all 0.2s ease;
  border: 1px solid #d0d7de;
  border-radius: 6px;
  background: white;
  cursor: pointer;

  &:hover {
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.12);
    border-color: #b0b7be;
    transform: translateY(-1px);
  }

  ::v-deep .card-body {
    padding: 0.75rem !important;
  }

  .task-title {
    font-size: 0.875rem;
    font-weight: 600;
    line-height: 1.5;
    word-break: break-word;
    // color: #24292f;
    margin-bottom: 0.5rem;
  }

  .task-description {
    font-size: 0.8125rem;
    line-height: 1.5;
    word-break: break-word;
    margin-bottom: 0.75rem;

    ::v-deep hr {
      border: none;
      border-top: 1px solid #d0d7de;
      margin: 0.5rem 0;
    }

    ::v-deep .mention {
      background-color: #dbeafe !important;
      color: #1e40af !important;
      padding: 0.125rem 0.375rem;
      border-radius: 0.25rem;
      font-weight: 500;
      transition: background-color 0.15s;
    }
  }

  .task-meta {
    ::v-deep .badge {
      font-size: 0.75rem;
      font-weight: 500;
      padding: 0.25em 0.5em;
      border-radius: 12px;

      &.badge-light-secondary {
        background: #e1e4e8;
        color: #57606a;
      }

      &.badge-light-danger {
        background: #ffebe9;
        color: #cf222e;
      }
    }
  }

  ::v-deep .dropdown {
    .btn-link {
      padding: 0.25rem;

      &:hover {
        background: #f6f8fa;
        border-radius: 4px;
      }
    }
  }

  ::v-deep .b-avatar {
    border: 1px solid #d0d7de;
  }
}
</style>
